import { Component, ElementRef, Inject, Input, TemplateRef, ViewChild } from '@angular/core';
import {
  CrmDependencies,
  CrmDrawerComponent,
  CrmInjectionToken,
  ObjectType,
  SNACKBAR_DURATION,
  TranslationModule,
  VerticalNavCardComponent,
  VerticalNavTabComponent,
} from '@galaxy/crm/static';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';

import { MatIconModule } from '@angular/material/icon';

import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { GalaxyTableModule, Row } from '@vendasta/galaxy/table';

import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import {
  DeleteData,
  isSystemField,
  ListCustomFieldManagementTableComponent,
} from './custom-field-management-table.component';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { FormBuilder, FormGroupDirective, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';

import { ArchiveFieldSchemaRequestInterface, FieldType } from '@vendasta/crm';
import { BehaviorSubject, combineLatest, map, of, switchMap } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { CustomFieldsManagementTableChangesService } from './custom-fields-management-table-changes.service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { ExternalIdComponent, sanitizeName } from './external-id/external-id.component';
import { DasherizePipe } from './dasherize.pipe';

enum DeleteStatus {
  Skipped,
  Success,
  Failure,
}

interface ExtraNavTab {
  label: string;
  component: any;
  inputs: any;
}

@Component({
  selector: 'crm-custom-fields-page',
  templateUrl: './custom-field-management-page.component.html',
  styleUrls: ['./custom-field-management-page.component.scss'],
  imports: [
    CommonModule,
    CrmDrawerComponent,
    MatButtonModule,
    TranslateModule,
    TranslationModule,
    GalaxyButtonLoadingIndicatorModule,
    GalaxyFormFieldModule,
    MatIconModule,
    VerticalNavTabComponent,
    VerticalNavCardComponent,
    MatTabGroup,
    MatTab,
    GalaxyTableModule,
    GalaxyLoadingSpinnerModule,
    GalaxyEmptyStateModule,
    ListCustomFieldManagementTableComponent,
    GalaxyPageModule,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatInput,
    GalaxyAlertModule,
    ExternalIdComponent,
    DasherizePipe,
  ],
  providers: [CustomFieldsManagementTableChangesService],
})
export class ListCustomFieldManagementPageComponent {
  @ViewChild('drawer') drawer?: CrmDrawerComponent;
  @ViewChild('upsert') upsertTemplateRef?: TemplateRef<any>;
  @ViewChild('form') formGroupDirective!: FormGroupDirective;
  @ViewChild('defaultInput') defaultInput?: ElementRef<HTMLTextAreaElement>;
  @Input({ required: false }) extraNavs: ExtraNavTab[] = [];

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly snackService: SnackbarService,
    private readonly translateService: TranslateService,
    private readonly tableChangesService: CustomFieldsManagementTableChangesService,
    private readonly modalService: OpenConfirmationModalService,
  ) {}

  action$$ = new BehaviorSubject<string | null>(null);
  editObjectId$$ = new BehaviorSubject<string | null>(null);
  isSystemField$$ = new BehaviorSubject<boolean>(false);
  externalIdPristine = true;

  isEditingDisabled$ = combineLatest([this.editObjectId$$, this.isSystemField$$]).pipe(
    map(([editObjectId, isSystemField]) => !!editObjectId || isSystemField),
  );

  formGroup = new FormBuilder().nonNullable.group({
    fieldName: ['', Validators.required],
    fieldType: ['', Validators.required],
    fieldDescription: '',
    objectType: ['Company', Validators.required],
    externalId: '',
  });

  createDrawer() {
    //reset all fields except for Object type
    this.formGroup.controls['fieldName'].reset();
    this.formGroup.controls['fieldDescription'].reset();
    this.formGroup.controls['fieldType'].reset();
    this.formGroup.controls['externalId'].reset();

    Object.keys(this.formGroup.controls).forEach((controlName) => {
      this.formGroup.controls[controlName].enable();
    });
    this.action$$.next('create');
    this.editObjectId$$.next('');
    this.isSystemField$$.next(false);
    this.externalIdPristine = true;
    if (this.drawer && this.upsertTemplateRef) {
      this.drawer.open(this.upsertTemplateRef, this.translateService.instant('CUSTOM_FIELDS.CREATE'));
    }
  }

  editDrawer(row: Row) {
    this.editObjectId$$.next(row.id);
    this.action$$.next('edit');

    this.formGroup.controls['fieldType'].setValue(row.data['field_type']?.value);
    this.formGroup.controls['fieldDescription'].setValue(row.data['description']?.value);
    this.formGroup.controls['fieldName'].setValue(row.data['name']?.value);
    this.formGroup.controls['externalId'].setValue(row.data['external_id']?.value);

    this.formGroup.controls['objectType'].disable();
    this.formGroup.controls['fieldType'].disable();

    if (isSystemField(row.data['namespace']?.value)) {
      this.formGroup.controls['fieldName'].disable();
      this.formGroup.controls['fieldDescription'].disable();
      this.isSystemField$$.next(true);
    } else {
      this.isSystemField$$.next(false);
      // Enable 'fieldName' and 'fieldDescription' if namespace is not 'standard', 'system', or 'extension'
      this.formGroup.controls['fieldName'].enable();
      this.formGroup.controls['fieldDescription'].enable();
    }
    if (this.drawer && this.upsertTemplateRef) {
      this.drawer.open(this.upsertTemplateRef, this.translateService.instant('CUSTOM_FIELDS.EDIT'));
    }
  }

  confirmDelete(data: DeleteData) {
    const dialogResult$ = this.modalService.openModal({
      type: 'warn',
      title: 'CUSTOM_FIELDS.DELETE_MODAL.TITLE',
      message: 'CUSTOM_FIELDS.DELETE_MODAL.DESCRIPTION',
      confirmButtonText: 'ACTIONS.DELETE',
      hideCancel: false,
      cancelButtonText: 'ACTIONS.CANCEL',
      actionOnEnterKey: true,
      cancelOnEscapeKeyOrBackgroundClick: true,
      width: 560,
    });

    dialogResult$
      .pipe(
        switchMap((result) => {
          if (!result) {
            return of(DeleteStatus.Skipped);
          }
          return this.tableChangesService
            .deleteField({
              namespace: data.namespace,
              crmObjectType: data.objectType,
              fieldId: data.fieldId,
            } as ArchiveFieldSchemaRequestInterface)
            .pipe(
              map((deleted) => {
                return deleted ? DeleteStatus.Success : DeleteStatus.Failure;
              }),
            );
        }),
      )
      .subscribe((wasDeleted) => {
        switch (wasDeleted) {
          case DeleteStatus.Skipped:
            return;
          case DeleteStatus.Success:
            this.snackService.openSuccessSnack('CUSTOM_FIELDS.SUCCESS.DELETED', {
              duration: SNACKBAR_DURATION,
            });
            return;
          case DeleteStatus.Failure:
            this.snackService.openErrorSnack('CUSTOM_FIELDS.ERROR.FAILED_TO_DELETE', {
              duration: SNACKBAR_DURATION,
            });
            return;
          default:
            return;
        }
      });
  }

  createField() {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      this.snackService.openErrorSnack('CUSTOM_FIELDS.ERROR.INVALID_FORM', { duration: SNACKBAR_DURATION });
      return;
    }
    const fieldType = this.readableTextToValidFieldType(this.formGroup.value.fieldType);
    const externalId = this.externalIdPristine
      ? sanitizeName(this.formGroup.value.fieldName)
      : this.formGroup.value.externalId;
    this.tableChangesService
      .createField(this.formGroup.value.objectType as ObjectType, {
        fieldName: this.formGroup.value.fieldName,
        fieldType: fieldType,
        fieldDescription: this.formGroup.value.fieldDescription,
        externalId: externalId,
      })
      .subscribe((created) => {
        if (created) {
          this.snackService.openSuccessSnack('CUSTOM_FIELDS.SUCCESS.CREATED', {
            duration: SNACKBAR_DURATION,
          });
        } else {
          this.snackService.openErrorSnack('CUSTOM_FIELDS.ERROR.FAILED_TO_CREATE', {
            duration: SNACKBAR_DURATION,
          });
        }
        this.drawer?.close();
      });
  }

  editField() {
    this.formGroup.controls['objectType'].enable();
    this.formGroup.controls['fieldType'].enable();
    if (this.formGroup.invalid) {
      this.snackService.openErrorSnack('CUSTOM_FIELDS.ERROR.INVALID_FORM', { duration: SNACKBAR_DURATION });
      return;
    }

    const fieldType = this.readableTextToValidFieldType(this.formGroup.value.fieldType);
    this.tableChangesService
      .updateField(this.formGroup.value.objectType as ObjectType, {
        fieldId: this.editObjectId$$.value ?? '',
        fieldName: this.formGroup.value.fieldName,
        fieldType: fieldType,
        fieldDescription: this.formGroup.value.fieldDescription,
        externalId: this.formGroup.value.externalId,
      })
      .subscribe((edited) => {
        if (edited) {
          this.snackService.openSuccessSnack('CUSTOM_FIELDS.SUCCESS.EDITED', {
            duration: SNACKBAR_DURATION,
          });
        } else {
          this.snackService.openErrorSnack('CUSTOM_FIELDS.ERROR.FAILED_TO_EDIT', {
            duration: SNACKBAR_DURATION,
          });
        }
        this.drawer?.close();
      });
  }
  validFieldTypeToReadableText(fieldType: FieldType): string {
    switch (fieldType) {
      case FieldType.FIELD_TYPE_DATE:
        return 'Date';
      case FieldType.FIELD_TYPE_INTEGER:
        return 'Integer';
      case FieldType.FIELD_TYPE_STRING:
        return 'String';
      case FieldType.FIELD_TYPE_GEOPOINT:
        return 'Geopoint';
      case FieldType.FIELD_TYPE_FLOAT:
        return 'Float';
      case FieldType.FIELD_TYPE_BOOLEAN:
        return 'Boolean';
      case FieldType.FIELD_TYPE_PHONE:
        return 'Phone number';
      case FieldType.FIELD_TYPE_EMAIL:
        return 'Email';
      case FieldType.FIELD_TYPE_DATETIME:
        return 'Date time';
      default:
        return '';
    }
  }
  readableTextToValidFieldType(fieldType: string): FieldType {
    switch (fieldType) {
      case 'Date':
        return FieldType.FIELD_TYPE_DATE;
      case 'Integer':
        return FieldType.FIELD_TYPE_INTEGER;
      case 'String':
        return FieldType.FIELD_TYPE_STRING;
      case 'Geopoint':
        return FieldType.FIELD_TYPE_GEOPOINT;
      case 'Float':
        return FieldType.FIELD_TYPE_FLOAT;
      case 'Boolean':
        return FieldType.FIELD_TYPE_BOOLEAN;
      case 'Phone number':
        return FieldType.FIELD_TYPE_PHONE;
      case 'Email':
        return FieldType.FIELD_TYPE_EMAIL;
      case 'Date time':
        return FieldType.FIELD_TYPE_DATETIME;
      default:
        return FieldType.FIELD_TYPE_INVALID;
    }
  }

  protected readonly FieldType = FieldType;
  currentTabClicked = '';

  tabClicked(event: string) {
    if (event == 'companies') {
      this.formGroup.controls['objectType'].setValue('Company');
    } else if (event == 'contacts') {
      this.formGroup.controls['objectType'].setValue('Contact');
    }
    this.currentTabClicked = event;
  }

  externalIdChanged(newExternalIdValue: string) {
    this.externalIdPristine = false;
    this.formGroup.controls['externalId'].setValue(newExternalIdValue);
  }

  externalIdErrorsChanged(errors: ValidationErrors | null) {
    this.formGroup.controls['externalId'].setErrors(errors);
  }
}
